/* eslint-disable react-hooks/exhaustive-deps */
import ReactLoading from 'react-loading';
import Cookies from 'js-cookie';
import api from 'api';
import styled from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import { Sidebar } from '../style';
import { Button } from 'components';
import { primary } from 'styles/colorProvider';
import { formatCurrency, formatZipCode } from 'react-data-formatter';
import { toast } from 'react-toastify';
import { FidelityDiscountLabelByModality } from 'utils/policyModality';
import { shouldDisplayFidelityDiscount } from 'utils/premiumDisplay';
import { getCurrentSubdomain } from 'utils/general';
import { getAnnualInstallmentInfo, shouldShowAnnualContractValues, shouldShowAnnualInstallments } from './helper';
import {
  getCommissionLimitAdjustment,
  getCommissionPayloadValue,
  getSidebarCommissionValue,
  shouldDisplayCommissionInSidebar,
} from '../commission';
import { buildQuotationCoveragePayload, getSanitizedCoverageRest, withIdCoverageCombo } from '../../../proposalPayload';

const AsideContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: ${({ mediaQuerieMaxWidth }) => mediaQuerieMaxWidth}) {
    flex: 1 1 auto;
  }
`;

export const SideBar = ({
  type,
  confirmButtonLabel = 'Enviar proposta',
  confirmButtonCollor = '',
  confirmButtonHandleClick = () => {},
  haveVehicleData = true,
  hasChange,
  setHasChange,
  mediaQuerieMaxWidth = '824px',
  quotationData,
  setQuotationData,
  setQuotationResponse,
  quotationResponse,
  dataCoverage,
  confirmButtonIsDisable,
  proposalResponse,
  nextStep,
  sliderHandle,
  percentCommission,
  setPercentCommission,
  percentDiscount,
  grievance_percent,
  min_grievance_percent,
  max_grievance_percent,
  discount_percent,
  selectedProducts,
  handleProducts,
  getPartnerProducts,
  useRangeSlider,
  installments,
  selectedInstallmentOption,
  shouldUseAnnualInstallments,
  onQuotationCalculationStateChange,
  canCalculateQuotation,
  requiresBasicCoverage = true,
}) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const [commission, setCommission] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [valueCoverage, setValueCoverage] = useState(0);
  const [coverages, setCoverages] = useState([]);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const quotationCalculationRequestId = useRef(0);
  const hasRequiredCoverage = requiresBasicCoverage
    ? Boolean(dataCoverage?.id_basic_coverage || dataCoverage?.id_coverage_combo)
    : Object.values(dataCoverage?.optional_coverages || {}).some(Boolean);
  const hasBasicCoverage = Boolean(quotationResponse?.basicCoverage);
  const [fidelityDiscount, setFidelityDiscount] = useState(0);
  const [annualInstallmentInfo, setAnnualInstallmentInfo] = useState(null);
  const shouldDisplayAnnualInstallments = shouldShowAnnualInstallments({
    shouldUseAnnualInstallments,
    type,
    policyModality: quotationData?.policy_modality,
    selectedInstallmentOption,
    annualInstallmentInfo,
  });
  const selectedAnnualInstallmentInfo = getAnnualInstallmentInfo({
    shouldDisplayAnnualInstallments,
    selectedInstallmentOption,
    annualInstallmentInfo,
  });

  const formatCurrencyValue = (value) =>
    Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

  const commissionPayloadValue = getCommissionPayloadValue({
    percentCommission,
    grievancePercent: grievance_percent,
    minGrievancePercent: min_grievance_percent,
    maxGrievancePercent: max_grievance_percent,
    useAbsoluteCommission: useRangeSlider,
  });

  const handleCommissionLimitError = (errorData) => {
    const adjustment = getCommissionLimitAdjustment({
      errorData,
      currentCommission: percentCommission,
      grievancePercent: grievance_percent,
      useAbsoluteCommission: useRangeSlider,
    });
    if (!adjustment) return false;

    setPercentCommission(adjustment.sliderValue);
    toast.warning(`Limite de comissão ajustado para ${adjustment.commissionPercent}% conforme permissão do grupo.`, {
      position: toast.POSITION.TOP_RIGHT,
      theme: 'colored',
    });
    return true;
  };

  const handleCalculateQuotation = async (type) => {
    const requestId = ++quotationCalculationRequestId.current;

    if (!canCalculateQuotation || !hasRequiredCoverage) {
      setLoadingQuotation(false);
      onQuotationCalculationStateChange?.({ loading: false, error: false });
      return;
    }

    let calculationFailed = false;

    setLoadingQuotation(true);
    onQuotationCalculationStateChange?.({ loading: true, error: false });
    const { rest, optionalsCoverages } = getSanitizedCoverageRest(dataCoverage);
    const data = buildQuotationCoveragePayload({
      quotationData,
      rest,
      optionalsCoverages,
      commissionPayloadValue,
      percentDiscount,
      discount_percent,
      policy_modality: quotationData?.policy_modality,
      installments,
      shouldUseAnnualInstallments,
    });

    try {
      const resp = await api({
        method: 'post',
        url: 'quotation/new-quotation',
        data,
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitriskwl-authtoken'),
        },
      });

      const respData = resp.data;
      if (requestId !== quotationCalculationRequestId.current) return;

      setQuotationResponse?.(respData);
      setValueCoverage(respData.valueCoverage);
      setDiscount(respData.discountValue);
      setCommission(getSidebarCommissionValue(respData));
      const coverages = [];
      if (respData.basicCoverage?.id) {
        coverages.push(respData.basicCoverage);
      }
      if (respData.suportCoverage?.id) {
        coverages.push(respData.suportCoverage);
      }
      if (respData.replacementCarCoverage?.id) {
        coverages.push(respData.replacementCarCoverage);
      }
      if (respData.glassCoverage?.id) {
        coverages.push(respData.glassCoverage);
      }
      if (respData.optionalCoverages.length > 0) {
        coverages.push(...respData.optionalCoverages);
      }

      if (respData.fidelityDiscount) {
        setFidelityDiscount(respData.fidelityDiscount);
      }

      if (shouldUseAnnualInstallments && respData.annualTotalValue) {
        setAnnualInstallmentInfo({
          annualTotal: respData.annualTotalValue,
          installmentValue: respData.installmentValue,
          installments: respData.installments,
        });
      } else {
        setAnnualInstallmentInfo(null);
      }

      setCoverages(coverages);
    } catch (error) {
      if (requestId !== quotationCalculationRequestId.current) return;

      calculationFailed = true;
      const errorData = error?.response?.data;
      const errorMessage = errorData?.error;
      const isLimitError = handleCommissionLimitError(errorData);

      if (!isLimitError) {
        const title = errorMessage || 'Erro ao criar cotação';
        toast.error(title, {
          position: toast.POSITION.TOP_RIGHT,
          theme: 'colored',
        });
      }
    } finally {
      if (requestId === quotationCalculationRequestId.current) {
        setLoadingQuotation(false);
        onQuotationCalculationStateChange?.({ loading: false, error: calculationFailed });
      }
    }
  };

  const getOldQuotation = async () => {
    setLoadingQuotation(true);
    try {
      const id = quotationData.quotationProposalData.quotationProposalId;
      const resp = await api({
        method: 'get',
        url: `quotation/new-quotation-proposal/${id}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitriskwl-authtoken'),
        },
      });

      const respData = resp.data;

      if (respData?.annualTotalValue) {
        setAnnualInstallmentInfo({
          annualTotal: respData.annualTotalValue,
          installmentValue: respData.installmentValue,
          installments: respData.installments,
        });
      } else {
        setAnnualInstallmentInfo(null);
      }

      if (type === 'proposal') {
        setQuotationResponse?.((previous) => ({ ...previous, ...respData }));
      }

      if (respData?.products?.length && handleProducts) {
        handleProducts(respData.products);
      }

      if (!respData?.valueCoverage) return;
      setValueCoverage(respData.valueCoverage);
      setDiscount(respData.discountValue);
      setCommission(getSidebarCommissionValue(respData));
      const coverages = [];
      if (respData.basicCoverage?.id) {
        coverages.push(respData.basicCoverage);
      }
      if (respData.suportCoverage?.id) {
        coverages.push(respData.suportCoverage);
      }
      if (respData.replacementCarCoverage?.id) {
        coverages.push(respData.replacementCarCoverage);
      }
      if (respData.glassCoverage?.id) {
        coverages.push(respData.glassCoverage);
      }
      if (respData.optionalCoverages.length > 0) {
        coverages.push(...respData.optionalCoverages);
      }

      setCoverages(coverages);
      setQuotationData((prev) => ({
        ...prev,
        policy_modality: respData?.policy_modality || prev?.policy_modality,
        installments: respData?.installments || prev?.installments,
      }));
    } catch (error) {
      const title = error?.response?.data?.error || 'Erro ao criar cotação';
      toast.error(title, {
        position: toast.POSITION.TOP_RIGHT,
        theme: 'colored',
      });
    } finally {
      setLoadingQuotation(false);
    }
  };

  const finalPremium =
    coverages?.length > 0 ? coverages?.reduce((acumulador, objeto) => Number(acumulador) + Number(objeto?.finalPrice), 0) : 0;
  const finalProductsValue =
    selectedProducts?.length > 0
      ? selectedProducts?.reduce((acc, obj) => {
          if (obj?.value_type === 'percentage') {
            return Number(acc) + (Number(finalPremium) * Number(obj?.value)) / 100;
          } else {
            return Number(acc) + Number(obj?.value);
          }
        }, 0)
      : 0;
  const shouldDisplayContractCoverageValues = shouldShowAnnualContractValues(quotationResponse || {});
  const displayedPremiumValue = shouldDisplayContractCoverageValues ? quotationResponse.premiumSummary.total : finalPremium;
  const displayedCommissionValue = getSidebarCommissionValue(quotationResponse, commission);
  const displayedDiscountValue = quotationResponse?.premiumSummary?.commercialDiscount ?? discount;
  const showCommissionValue = shouldDisplayCommissionInSidebar(getCurrentSubdomain()) && displayedCommissionValue > 0;
  const totalValue =
    shouldDisplayAnnualInstallments && selectedAnnualInstallmentInfo
      ? selectedAnnualInstallmentInfo.annualTotal
      : (finalPremium || 0) + (finalProductsValue || 0);
  const shouldDisplayFidelity = shouldDisplayFidelityDiscount({
    capabilities: quotationResponse?.capabilities,
    discountType: 'fidelity',
    discount: fidelityDiscount,
    modality: quotationData?.policy_modality,
  });

  const handleSaveQuotation = async () => {
    setLoadingQuotation(true);
    if (!hasRequiredCoverage) {
      toast.error(requiresBasicCoverage ? 'Selecione uma cobertura basica' : 'Selecione ao menos uma cobertura', {
        position: toast.POSITION.TOP_RIGHT,
        theme: 'colored',
      });
      setLoadingQuotation(false);
      return false;
    }
    const { rest, optionalsCoverages } = getSanitizedCoverageRest(dataCoverage);

    const productIds = selectedProducts?.length > 0 ? selectedProducts?.map((i) => i.id) : undefined;

    const data = buildQuotationCoveragePayload({
      quotationData,
      rest,
      optionalsCoverages,
      commissionPayloadValue,
      percentDiscount,
      discount_percent,
      policy_modality: quotationData.policy_modality,
      installments,
      shouldUseAnnualInstallments,
      productIds,
    });
    try {
      const id = quotationData.quotationProposalData.quotationProposalId;

      const quotation = await api({
        method: 'put',
        data: withIdCoverageCombo(data),
        url: `/quotation/proposal-products/${id}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitriskwl-authtoken'),
        },
      });

      const respData = quotation.data;
      if (shouldUseAnnualInstallments && respData.annualTotalValue) {
        setAnnualInstallmentInfo({
          annualTotal: respData.annualTotalValue,
          installmentValue: respData.installmentValue,
          installments: respData.installments,
        });
      } else {
        setAnnualInstallmentInfo(null);
      }

      setHasChange(false);
      getPartnerProducts();
      return true;
    } catch (error) {
      const title = error?.response?.data?.error || 'Erro ao criar cotação';
      toast.error(title, {
        position: toast.POSITION.TOP_RIGHT,
        theme: 'colored',
      });
      return false;
    } finally {
      setLoadingQuotation(false);
    }
  };

  const saveAndPrintQuotation = async (type) => {
    if (hasChange) {
      const saved = await handleSaveQuotation();
      if (!saved) return;
    }
    const id = quotationData.quotationProposalData.quotationProposalId;
    window.open(type === 'quotation' ? `/imprimir-cotacao/${id}` : `/imprimir-cotacao-documento-complementar/${id}`, '_blank');
  };

  const handleSubmit = async () => {
    try {
      setLoadingQuotation(true);

      await handleVerifyVehicle();

      if (hasChange) {
        const saved = await handleSaveQuotation();
        if (!saved) return;
      }

      nextStep();
    } catch (error) {
    } finally {
      setLoadingQuotation(false);
    }
  };

  const handleVerifyVehicle = async () => {
    try {
      const plate = quotationData.vehicle?.plate;
      const chassi = quotationData.vehicle?.chassi;
      await api({
        method: 'post',
        url: '/quotation/verify-vehicle',
        data: { chassi, plate },
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('splitriskwl-authtoken'),
        },
      });
    } catch (error) {
      toast.error(error?.response?.data?.error, {
        position: toast.POSITION.TOP_RIGHT,
        theme: 'colored',
      });
      throw error;
    }
  };

  useEffect(() => {
    handleCalculateQuotation();

    return () => {
      quotationCalculationRequestId.current += 1;
    };
  }, [
    dataCoverage,
    percentCommission,
    percentDiscount,
    installments,
    quotationData?.policy_modality,
    shouldUseAnnualInstallments,
    canCalculateQuotation,
  ]);
  useEffect(() => {
    if (!hasChange) {
      getOldQuotation();
    }
  }, [hasChange]);

  return (
    <AsideContainer mediaQuerieMaxWidth={mediaQuerieMaxWidth}>
      {/* <h1>{`${hasChange}`}</h1> */}
      <Sidebar>
        {loadingQuotation && (
          <>
            <h4>Valor total</h4>
            <div style={{ width: '100%', boxSizing: 'border-box', padding: 20 }}>
              <ReactLoading color={primary} height={24} width={24} type="spin" />
            </div>
          </>
        )}

        {!loadingQuotation && coverages.length > 0 && (
          <>
            <h4>Valor total</h4>
            <p className="value">{formatCurrencyValue(totalValue)}</p>
            {shouldDisplayAnnualInstallments && selectedAnnualInstallmentInfo && (
              <p style={{ marginTop: 10 }}>
                <span className="value-label">Parcela:</span> {selectedAnnualInstallmentInfo.installments}x de{' '}
                {formatCurrencyValue(selectedAnnualInstallmentInfo.installmentValue)}
              </p>
            )}
            {(showCommissionValue || sliderHandle) && (
              <div>
                {showCommissionValue && (
                  <p>
                    {formatCurrencyValue(displayedCommissionValue)}{' '}
                    <span className="value-label">
                      / {shouldDisplayContractCoverageValues ? 'Comissão do contrato' : 'Comissão'}
                    </span>
                  </p>
                )}
                {sliderHandle && displayedDiscountValue > 0 && (
                  <p>
                    {formatCurrencyValue(displayedDiscountValue)}{' '}
                    <span className="value-label">
                      / {shouldDisplayContractCoverageValues ? 'Desconto comercial do contrato' : 'Desconto'}
                    </span>
                  </p>
                )}
              </div>
            )}
            {shouldDisplayFidelity && (
              <>
                <p>
                  Tipo de desconto: <span className="value-label">Fidelidade</span>
                </p>
                <p style={{ color: 'red' }}>
                  Será aplicado {fidelityDiscount}% de desconto na{' '}
                  {FidelityDiscountLabelByModality[quotationData?.policy_modality]} parcela da Apólice
                </p>
              </>
            )}
          </>
        )}

        {selectedProducts?.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <h5>Valor prêmio</h5>
            <p className="value-label">
              {formatCurrencyValue(displayedPremiumValue)} <span className="value-label"></span>
            </p>

            <h5>Valor produtos</h5>
            <p className="value-label">
              {formatCurrency(finalProductsValue || 0)} <span className="value-label"></span>
            </p>
          </div>
        )}

        <h4 style={{ marginTop: 20 }}>Dados do veículo</h4>

        {type === 'quotation' && (
          <>
            <p>Modelo: {quotationData?.vehicle?.model}</p>
            <p>
              Ano modelo: {quotationData?.vehicle?.brand} {quotationData?.vehicle?.model?.split(' ')[0]}{' '}
              {quotationData?.vehicle?.year_model}
            </p>
            <p>Placa: {quotationData?.vehicle?.plate?.toUpperCase() || 'Não emplacado'}</p>
          </>
        )}
        <p>Valor FIPE: {formatCurrency(quotationData?.vehicle?.value || 0)}</p>
        {requiresBasicCoverage && (
          <p>
            LMI{' '}
            {(
              (quotationData?.vehicle?.auction || quotationData?.vehicle?.remarked ? valueCoverage * 70 : valueCoverage * 100) /
              quotationData?.vehicle?.value
            )?.toFixed(0)}
            %:{' '}
            {quotationData?.vehicle?.auction || quotationData?.vehicle?.remarked
              ? formatCurrency(valueCoverage * 0.7 || 0)
              : formatCurrency(valueCoverage || 0)}{' '}
          </p>
        )}
        <p>Chassis Remarcado/Veiculo Recuperado de Sinistro: {quotationData?.vehicle?.remarked ? 'Sim' : 'Não'}</p>
        <p>GNV: {quotationData?.vehicle?.gnv ? 'Sim' : 'Não'}</p>
        <p>Condutor entre 18 e 25 anos: {quotationData?.quotationProposalData?.under25 ? 'Sim' : 'Não'}</p>

        <h4 style={{ marginTop: 20 }}>Coberturas</h4>
        {coverages?.length <= 0 && (
          <small>
            <em>Nenhuma cobertura selecionada</em>
          </small>
        )}
        {coverages?.length > 0 &&
          coverages?.map((item) => (
            <div className="coverage-item">
              <p>{item?.name}</p>
              <p>
                {formatCurrencyValue(
                  shouldDisplayContractCoverageValues ? item?.contractValue ?? item?.finalPrice : item?.finalPrice,
                )}
              </p>
            </div>
          ))}

        {selectedProducts?.filter((item) => item?.value_type === 'value')?.length > 0 && (
          <>
            <h4>Produtos</h4>
            {selectedProducts
              ?.filter((item) => item?.value_type === 'value')
              ?.map((item) => (
                <div className="coverage-item">
                  <p>{item?.name}</p>
                  <p>{formatCurrency(item.value_type === 'percentage' ? (finalPremium * item.value) / 100 : item.value)}</p>
                </div>
              ))}
          </>
        )}

        {type === 'proposal' && (
          <>
            <h4>Endereço</h4>
            <p>CEP: {formatZipCode(quotationData?.address?.zip_code)}</p>
            <p>
              Endereço: {quotationData?.address?.street}{' '}
              {quotationData?.address?.number && <>, {quotationData?.address?.number}</>}
            </p>
            {quotationData?.address?.complement && <p>Complemento: {quotationData?.address?.complement}</p>}
            <p>
              Cidade/UF: {quotationData?.address?.city} {quotationData?.address?.state && <>/{quotationData?.address?.state}</>}
            </p>
          </>
        )}

        {type === 'quotation' && (
          <div className="button-area">
            <Button
              disabled={loadingSave || loadingQuotation}
              height="35px"
              onClick={handleSaveQuotation}
              buttonColor={confirmButtonCollor}
              buttonBoxShadowColor="transparent"
              borderRadius="10px"
            >
              {loadingSave ? <ReactLoading color="white" height={24} width={24} type="spin" /> : 'Salvar'}
            </Button>

            <Button
              disabled={loadingSave || loadingQuotation}
              height="35px"
              onClick={() => {
                saveAndPrintQuotation('quotation');
              }}
              buttonColor={confirmButtonCollor}
              buttonBoxShadowColor="transparent"
              borderRadius="10px"
            >
              {loadingSave ? <ReactLoading color="white" height={24} width={24} type="spin" /> : 'Cotação Apólice'}
            </Button>

            {selectedProducts?.length > 0 && (
              <Button
                disabled={loadingSave || loadingQuotation}
                height="35px"
                width="260px"
                onClick={() => {
                  saveAndPrintQuotation('document');
                }}
                buttonColor={confirmButtonCollor}
                buttonBoxShadowColor="transparent"
                borderRadius="10px"
              >
                {loadingSave ? <ReactLoading color="white" height={24} width={24} type="spin" /> : 'Documento Complementar'}
              </Button>
            )}

            <Button
              disabled={proposalResponse?.response?.id || confirmButtonIsDisable || loadingQuotation}
              height="35px"
              width="260px"
              // onClick={confirmButtonHandleClick}
              onClick={handleSubmit}
              buttonColor={confirmButtonCollor}
              buttonBoxShadowColor="transparent"
              borderRadius="10px"
            >
              {confirmButtonLabel}
            </Button>
          </div>
        )}
        {!hasBasicCoverage && requiresBasicCoverage && type !== 'proposal' && (
          <small style={{ marginTop: 15 }}>Por favor, selecione pelo menos uma cobertura básica para continuar.</small>
        )}
        {!hasRequiredCoverage && !requiresBasicCoverage && type !== 'proposal' && (
          <small style={{ marginTop: 15 }}>Por favor, selecione pelo menos uma cobertura para continuar.</small>
        )}
        {proposalResponse?.response?.id && <small style={{ marginTop: 15 }}>Proposta já emitida</small>}
      </Sidebar>
    </AsideContainer>
  );
};

export default SideBar;
